<template>
  <span class="asset-photo" :class="`asset-photo--${size}`">
    <!-- Under the photo, not instead of it, so a missing url and a dead one
         degrade identically. A camera by default: it stands for the picture
         that is missing, never for the thing it would have shown. `car` is the
         opt-in for a plate that names the KIND of thing (a dashboard row with
         no photo source at all), where a camera would promise one. -->
    <span class="asset-photo__placeholder" aria-hidden="true"><i class="pi" :class="`pi-${placeholderIcon}`" /></span>
    <img
      v-if="showsPhoto"
      class="asset-photo__image"
      :src="src"
      :alt="alt"
      :loading="loading"
      @error="failedSource = src"
    >
    <!-- Overlays ride on the photo: a run number, a LIVE badge, a lot index, a
         bid count. The layer covers the box and is the positioning context, so
         an overlay places itself with the same offsets it used on a
         hand-rolled thumb. -->
    <span v-if="$slots.overlay" class="asset-photo__overlay"><slot name="overlay" /></span>
  </span>
</template>

<script setup>
import { computed, ref } from 'vue';

// The one photo box every asset surface draws: a placeholder glyph under an
// optional photo, in the box the caller sizes, with an optional overlay layer. BidCard, BidSheet, AssetCard,
// BuyerAssetTile, LiveLaneCard and the asset detail gallery each used to draw
// their own, and two of them had drifted to a car glyph and one to a word
// plate. Presentation only: the caller owns the box, its radius and its
// background; this fills it.
defineOptions({ name: 'AssetPhoto' });

const props = defineProps({
  /** The photo's url. Null, empty, or a url that fails to load shows the camera. */
  src: { type: String, default: null },
  /** The photo's text alternative. Empty when the caller names the asset beside it. */
  alt: { type: String, default: '' },
  /** The image's loading hint. Eager for the first thing on screen, lazy below the fold. */
  loading: { type: String, default: 'lazy', validator: (value) => ['lazy', 'eager'].includes(value) },
  /**
   * How large the placeholder glyph draws: small in a thumbnail, medium on a
   * card, large in a gallery — or `fill`, which scales it with whatever box the
   * caller drew (a 36px row plate, a mosaic cell, a 62px stack thumb), so one
   * value serves every thumb whose size is the parent's business.
   */
  size: { type: String, default: 'medium', validator: (value) => ['small', 'medium', 'large', 'fill'].includes(value) },
  /** The placeholder glyph: `camera` (default — the missing picture) or `car`. */
  placeholderIcon: { type: String, default: 'camera', validator: (value) => ['camera', 'car'].includes(value) },
});

// A url that fails to load is remembered, so the camera shows for it and a
// new url gets its own attempt.
const failedSource = ref(null);
const showsPhoto = computed(() => Boolean(props.src) && props.src !== failedSource.value);
</script>

<style scoped>
.asset-photo {
  position: absolute;
  inset: 0;
  display: block;
}

.asset-photo__placeholder {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: var(--p-asset-photo-placeholder-color);
}

/* PrimeVue's base theme pins every `.pi` at the icon size (1rem), so a size set
   on the placeholder never reached the glyph and every camera drew at 16px. The
   glyph takes the placeholder's size, which the rules below set. */
.asset-photo__placeholder .pi { font-size: inherit; }

.asset-photo--small .asset-photo__placeholder { font-size: var(--p-asset-photo-small-placeholder-font-size); }
.asset-photo--medium .asset-photo__placeholder { font-size: var(--p-asset-photo-medium-placeholder-font-size); }
.asset-photo--large .asset-photo__placeholder { font-size: var(--p-asset-photo-large-placeholder-font-size); }

/* Fill: the glyph follows the box. A size container on the photo box itself
   (which already covers the caller's box), and the glyph a share of its
   shorter side, clamped so a tiny plate stays legible and a gallery does not
   draw a billboard. */
.asset-photo--fill { container-type: size; }
.asset-photo--fill .asset-photo__placeholder {
  font-size: clamp(
    var(--p-asset-photo-fill-placeholder-min-font-size),
    var(--p-asset-photo-fill-placeholder-share),
    var(--p-asset-photo-fill-placeholder-max-font-size)
  );
}

.asset-photo__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.asset-photo__overlay {
  position: absolute;
  inset: 0;
  display: block;
}
</style>
